import { stringify } from 'qs';
import moment from 'moment';
import * as pageTools from '@qompium/pages-helper-tools';
import * as Sentry from '@sentry/react';
import { showErrorMessage } from '../helpers/logger';
import { estimatedTimeBeforeRenewal, pagesBaseUrl } from '../config';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const HtmlToReactParser = require('html-to-react').Parser;

export const languages = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
  da: 'Danish',
};

export function getMessage(language: string, id: string) {
  return getMessages(language)[id];
}

export function getMessages(language: string) {
  try {
    return require(`../assets/locales/${language}.json`);
  } catch (err) {
    return require('../assets/locales/en.json');
  }
}

export const PagePath = {
  plans: '/',
  mySubscriptions: '/my_subscriptions',
  complete: '/complete',
  noB2C: '/no_b2c',
  userHasIap: '/iap',
};

export function redirectToUrl(uri: string) {
  window.location.href = uri;
}

export function redirectToLogin() {
  redirectToUrl(`${pageTools.url.pages}/login/?redirect=${encodeURIComponent(window.location.href)}`);
}

export function redirectWithQueryParams(path: string, queryParams: any = {}, extraParams: any = {}) {
  let href = `${window.location.origin}${window.location.pathname}#${path}`;

  let params: any = { ...extraParams };

  for (const [key, value] of Object.entries(queryParams)) {
    if (['origin', 'redirect', 'result'].includes(key)) {
      params = { ...params, [key]: value };
    }
  }

  if (Object.keys(params).length !== 0) {
    href += `?${stringify(params, { encode: false })}`;
  }
  redirectToUrl(href);
}

export function redirectToFAQ(language = 'en') {
  let lang = language;
  if (language === 'en') {
    lang = 'en-us';
  }
  if (language === 'fr') {
    lang = 'fr-be';
  }
  window.open(`https://help.fibricheck.com/hc/${lang}/sections/360001991119-Subscription-Plans`, '_target');
}

export function redirectToPayment(productId: string, query: any) {
  const redirectUri: any = String(query.redirect);
  const origin: any = String(query.origin);
  const params: any = {};
  if (redirectUri !== 'undefined' && validURL(redirectUri)) {
    params.redirect = redirectUri;
  }
  let originToCheckout = '';
  if (origin !== 'undefined') {
    params.origin = origin;
    originToCheckout = `&data.origin=${origin}`;
  }

  let paramString = '';
  if (Object.keys(params).length !== 0) {
    paramString += `?${stringify(params)}`;
  }

  window.location.href =
    `${`${pagesBaseUrl}/checkout/?productId=${productId}${originToCheckout}` +
    `&redirect=${pagesBaseUrl}/subscription/`}${
      encodeURIComponent(paramString)
    }#/my_subscriptions`;
}

export function redirectToLink(link: string, language = 'en') {
  window.open(`${link}${language}/`, '_target');
}

export function padStart(initialMonthNumber: number, _targetLength: number, _padString: string) {
  const initialMonthString = initialMonthNumber.toString();
  return /^\d$/.test(initialMonthString) ? `0${initialMonthString}` : initialMonthString;
}

export function convertFirstLettersToUppercase(text: string) {
  const initialArray = text.split(' ');
  const resultsArray = [];

  for (const word of initialArray) {
    resultsArray.push(word.charAt(0).toUpperCase() + word.slice(1));
  }
  return resultsArray.join(' ');
}

export function isMobilePhone() {
  return /iPhone|iPod|Android/i.test(navigator.userAgent);
}

export function isSafari() {
  return !!navigator.userAgent.match(/Version\/[\d.]+.*Safari/);
}

export function isSmallDevice() {
  return window.innerWidth <= 400;
}

export function isMobile() {
  return window.innerWidth <= 1370;
}

export function formatDate(epoch: number | undefined) {
  if (!epoch) {
    return '--/--/----';
  }
  const date = new Date(epoch);
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}/${date.getUTCFullYear()}`;
}

export function validURL(uri: string) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(uri);
}

export function resolveTextWithLinks(text: string | null, links: any): string {
  const targetText: any = text;

  let index = -1;
  const newTargetText = targetText.replace(/<a>/g, () => {
    index += 1;
    return `<a href="${links[index]}" target="_blank">`;
  });

  const htmlToReactParser = new HtmlToReactParser();
  const reactElement = htmlToReactParser.parse(newTargetText);
  return reactElement;
}

export function resolveTextWithHTML(text: string) {
  const targetText = text;

  const htmlToReactParser = new HtmlToReactParser();
  const reactElement = htmlToReactParser.parse(targetText);
  return reactElement;
}

export function calculateDifferenceInMonths(prescriptionStart: number) {
  const result = Math.floor(moment(new Date(Date.now())).diff(new Date(prescriptionStart), 'months', true));
  return result > 11 ? 11 : result;
}

export function timeBeforeRenewalIsPassed(nextPaymentTimestamp: number): boolean {
  return Date.now() > moment(nextPaymentTimestamp).subtract(Number(estimatedTimeBeforeRenewal), 'hours').valueOf();
}

export async function checkError(error: any, path: string, language = 'en') {
  const data = await error;
  if (data.code === 104 || data.code === 108) {
    if (path !== PagePath.plans) {
      redirectToLogin(); // redirect to login if not autherizate
    }
  } else {
    // LOG ERROR
    if (window.location.hostname !== 'localhost') {
      if (data) {
        Sentry.setExtras({ data });
      }
      Sentry.captureException(error);
    } else {
      // eslint-disable-next-line no-console
      console.error('SENTRY', error);
    }
    showErrorMessage(getMessage(language, 'payment_error'));
  }
}
